import React from "react";
import { Box, Text, Flex, VStack } from "@chakra-ui/react";

interface TextSectionProps {
  title: string;
  paragraphs: string[];
  layout?: "side-by-side" | "stacked" | "center-stacked";
  titlePosition?: "left" | "top" | "center";
  titleSize?: {
    base?: string;
    md?: string;
    lg?: string;
  };
  textSize?: {
    base?: string;
    md?: string;
    lg?: string;
  };
  spacing?: number;
  maxWidth?: string;
}

const TextSection: React.FC<TextSectionProps> = ({
  title,
  paragraphs,
  layout = "stacked",
  titlePosition = "top",
  titleSize = { base: "xl", md: "2xl", lg: "3xl" },
  textSize = { base: "md", md: "lg" },
  spacing = 6,
  maxWidth = "1400px",
}) => {
  // Determine if we should use side-by-side layout based on screen size and layout prop
  const shouldUseSideBySide = layout === "side-by-side";
  const isCenterStacked = layout === "center-stacked";

  // Title component
  const TitleComponent = (
    <Text
      fontSize={titleSize}
      fontWeight="bold"
      fontFamily="Abel"
      textAlign={
        isCenterStacked 
          ? "center" 
          : shouldUseSideBySide && titlePosition === "left" 
            ? "left" 
            : titlePosition === "center" 
              ? "center" 
              : "left"
      }
      mb={shouldUseSideBySide ? 0 : spacing}
      lineHeight="1.2"
    >
      {title}
    </Text>
  );

  // Paragraphs component
  const ParagraphsComponent = (
    <VStack spacing={4} align="stretch">
      {paragraphs.map((paragraph, index) => (
        <Text
          key={index}
          fontSize={textSize}
          fontFamily="Source Code Pro"
          lineHeight="1.6"
          textAlign={isCenterStacked ? "center" : "left"}
        >
          {paragraph}
        </Text>
      ))}
    </VStack>
  );

  return (
    <Box
      maxWidth={maxWidth}
      margin="0 auto"
      padding="20px"
      pr="5%"
      pl="5%"
    >
      {/* Side-by-side layout for desktop, stacked for mobile */}
      {shouldUseSideBySide ? (
        <Flex
          direction={{ base: "column", md: "row" }}
          gap={spacing}
          align={{ base: "stretch", md: "center" }}
        >
          {/* Title column */}
          <Box
            flex={{ base: "none", md: "0 0 40%" }}
            mb={{ base: spacing, md: 0 }}
          >
            {TitleComponent}
          </Box>
          
          {/* Text column */}
          <Box flex={{ base: "none", md: "1" }}>
            {ParagraphsComponent}
          </Box>
        </Flex>
      ) : (
        /* Stacked layout */
        <VStack spacing={spacing} align="stretch">
          {TitleComponent}
          {ParagraphsComponent}
        </VStack>
      )}
    </Box>
  );
};

export default TextSection;